import React, { useCallback, useMemo, useState } from 'react';
import {
  servicesConstants,
  useCallSuccess,
  useFeatureFlagsContext,
  useI18n,
  useServicesQuery,
} from '@jarvis/react-portal-addons';
import {
  IconHandWithPlant,
  IconPrinterInkDrop,
  IconSmartApp,
  IconTruck,
} from '@veneer/core';
import SolutionCardTemplate from './SolutionCardTemplate';
import { useCardsVisibility } from './useCardsVisibility';
import useOpenStoreForHpApp from '../../../hooks/useOpenStoreForHpApp';
import { DEEPLINKS } from '../../../utils/constants';
import {
  GetTheHpAppDownloadNowButton, InstantInkLearnMoreLink, InstantInkManagePlanLink, SustainabilityLearnMoreLink,
} from '../../../utils/analytics';

const {
  SERVICE_TYPES,
  SERVICE_LEVELS,
  COMMON_QUERIES,
} = servicesConstants;

const {
  INSTANT_INK_ALL_ENABLED_QUERY,
  INSTANT_INK_ALL_OFFERING_QUERY,
} = COMMON_QUERIES;

const SolutionCards = ({ deviceCache }) => {
  const [deviceIds, setDeviceIds] = useState([]);
  const { t } = useI18n();

  const {
    grantsSupport,
  } = useFeatureFlagsContext();

  const {
    showInstantInkCard,
    showHpAllInPlanCard,
    showSustainabilityCard,
    showGetHpAppCard,
    instantInkCardLinkToOverview,
  } = useCardsVisibility({ deviceCache });

  const query = useMemo(() => ({
    type: grantsSupport ? SERVICE_TYPES.GRANTS : SERVICE_TYPES.ENTITLEMENTS,
    level: SERVICE_LEVELS.ALL,
    deviceIds,
    services: [
      {
        ...INSTANT_INK_ALL_ENABLED_QUERY,
        id: 'InstantInkEnabled',
      },
      {
        ...INSTANT_INK_ALL_OFFERING_QUERY,
        id: 'InstantInkOffering',
      },
    ],
  }), [deviceIds, grantsSupport]);

  const {
    success,
    pending,
    error,
    helpers: {
      countDeviceServices,
    },
  } = useServicesQuery(query);

  const instantInkCardLoading = (!success && !error) || pending || deviceCache.pending;
  const instantInkEligiblePrinterCounter = countDeviceServices({ id: 'InstantInkOffering' });
  const instantInkEnrolledPrinterCounter = countDeviceServices({ id: 'InstantInkEnabled' });

  const instantInkCardDependenciesReady = showInstantInkCard;

  const showInstantInkEnrolledCard = instantInkCardDependenciesReady && instantInkEnrolledPrinterCounter > 0;

  const showInstantInkEligibleCard = instantInkCardDependenciesReady && !showInstantInkEnrolledCard;

  const onDeviceCacheSuccess = useCallback(
    (_, devices) => {
      setDeviceIds(devices.map(({ deviceId }) => deviceId));
    },
    [],
  );

  useCallSuccess({
    call: deviceCache,
    onSuccess: onDeviceCacheSuccess,
  });

  const { openStoreForHpApp } = useOpenStoreForHpApp();

  const CardList = useMemo(() => [
    showGetHpAppCard
    && {
      analyticsData: GetTheHpAppDownloadNowButton,
      headerText: t('dashboard.getTheHpAppCard.header'),
      title: t('dashboard.getTheHpAppCard.title'),
      description: t('dashboard.getTheHpAppCard.description'),
      icon: <IconSmartApp filled />,
      onClick: openStoreForHpApp,
      footerText: t('dashboard.getTheHpAppCard.footer'),
      dataTestIds: {
        card: 'get-the-hp-app-card',
        header: 'get-the-app-card-header',
        title: 'get-the-app-card-title',
        description: 'get-the-app-card-description',
        link: 'get-the-app-card-link',
      },
    },
    showInstantInkEnrolledCard
    && {
      analyticsData: InstantInkManagePlanLink,
      headerText: instantInkEnrolledPrinterCounter === 1 ? t('dashboard.instantInkEnrolledCard.headerSingular', {
        totalPrinters: deviceCache?.data?.length || 0,
      }) : t('dashboard.instantInkEnrolledCard.headerPlural', {
        totalEnrolled: instantInkEnrolledPrinterCounter,
        totalPrinters: deviceCache?.data?.length || 0,
      }),
      title: t('dashboard.instantInkEnrolledCard.title'),
      description: t('dashboard.instantInkEnrolledCard.description'),
      icon: <IconTruck filled />,
      footerHyperlink: instantInkCardLinkToOverview ? DEEPLINKS.INSTANT_INK_OVERVIEW : DEEPLINKS.INSTANT_INK,
      footerText: t('dashboard.instantInkEnrolledCard.footer'),
      showCheckmarkCircleIcon: true,
      dataTestIds: {
        card: 'instant-ink-enrolled-card',
        header: 'instant-ink-enrolled-card-header',
        title: 'instant-ink-enrolled-card-title',
        description: 'instant-ink-enrolled-card-description',
        link: 'instant-ink-enrolled-card-link',
      },
      loading: instantInkCardLoading,
    },
    showInstantInkEligibleCard
    && {
      analyticsData: InstantInkLearnMoreLink,
      headerText: instantInkEligiblePrinterCounter === 1 ? t('dashboard.instantInkEligibleCard.headerSingular', {
        totalEligible: instantInkEligiblePrinterCounter,
      }) : t('dashboard.instantInkEligibleCard.headerPlural', {
        totalEligible: instantInkEligiblePrinterCounter,
      }),
      title: t('dashboard.instantInkEnrolledCard.title'),
      description: t('dashboard.instantInkEligibleCard.description'),
      icon: <IconTruck filled />,
      footerHyperlink: instantInkCardLinkToOverview ? DEEPLINKS.INSTANT_INK_OVERVIEW : DEEPLINKS.INSTANT_INK,
      footerText: t('dashboard.instantInkEligibleCard.footer'),
      dataTestIds: {
        card: 'instant-ink-eligible-card',
        header: 'instant-ink-eligible-card-header',
        title: 'instant-ink-eligible-card-title',
        description: 'instant-ink-eligible-card-description',
        link: 'instant-ink-eligible-card-link',
      },
      loading: instantInkCardLoading,
    },
    showHpAllInPlanCard
    && {
      headerText: 'Printer, Ink and 24/7 Support',
      title: 'HP All-in Plan',
      description: `Enjoy hassle-free printing with the HP All-In Plan.
      Get all your printing essentials for one low monthly payment.`,
      icon: <IconPrinterInkDrop filled />,
      footerHyperlink: '/',
      footerText: 'Learn more',
      dataTestIds: {
        card: 'hp-all-in-plan-card',
        header: 'hp-all-in-plan-card-header',
        title: 'hp-all-in-plan-card-title',
        description: 'hp-all-in-plan-card-description',
        link: 'hp-all-in-plan-card-link',
      },
    },
    showSustainabilityCard
    && {
      analyticsData: SustainabilityLearnMoreLink,
      headerText: t('dashboard.sustainabilityCard.header'),
      title: t('dashboard.sustainabilityCard.title'),
      description: t('dashboard.sustainabilityCard.description'),
      icon: <IconHandWithPlant filled />,
      footerHyperlink: '/forestfirst',
      footerText: t('dashboard.sustainabilityCard.footer'),
      dataTestIds: {
        card: 'sustainability-card',
        header: 'sustainability-card-header',
        title: 'sustainability-card-title',
        description: 'sustainability-card-description',
        link: 'sustainability-card-link',
      },
    },
  ].filter(Boolean), [
    deviceCache?.data?.length,
    instantInkCardLinkToOverview,
    instantInkCardLoading,
    instantInkEligiblePrinterCounter,
    instantInkEnrolledPrinterCounter,
    openStoreForHpApp,
    showGetHpAppCard,
    showHpAllInPlanCard,
    showInstantInkEligibleCard,
    showInstantInkEnrolledCard,
    showSustainabilityCard,
    t,
  ]);

  if (!CardList.length) {
    return null;
  }

  return CardList.map(cardInfo => (
    <SolutionCardTemplate
      key={cardInfo.dataTestIds.card} {...cardInfo}
      data-testid={cardInfo.dataTestIds.card}
    />
  ));
};

export default SolutionCards;
